import React, { useEffect, useRef, useState } from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { Button } from '@noths/polaris-design-system-core-components';
import dynamic from 'next/dynamic';

import { usePrefersReducedMotion } from 'src/components/organisms/HeroCarousel/usePrefersReducedMotion';
import { PlayIcon } from './icons';
import * as styles from './HeroVideo.styles';

const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), { ssr: false });

export interface HeroVideoButton {
  href: string;
  text: string;
}

export interface HeroVideoProps {
  buttons: HeroVideoButton[];
  headingLine1: string;
  headingLine2?: string;
  /**
   * `hidden` keeps the heading in the document for screen readers and search engines but clips it
   * out of view. For footage that already carries its own headline.
   */
  headingPosition: 'top' | 'bottom' | 'hidden';
  onCtaClick?: (button: HeroVideoButton, e: React.MouseEvent<HTMLAnchorElement>) => void;
  onWatchNowClick?: () => void;
  paragraph?: string;
  /** Pause the preview loop, e.g. while the takeover plays the full film over it. */
  paused?: boolean;
  previewPlaybackId: string;
  /**
   * Per-breakpoint still crops, built server side by the content transformer so they land in the
   * SSR HTML (the still is the LCP element). When absent (e.g. Storybook), the uncropped opening
   * frame is derived from the playback id instead.
   */
  stillImageUrlLarge?: string;
  stillImageUrlSmall?: string;
  termsAndConditions?: string;
  /** Reported to Mux Data so views can be filtered by film. */
  videoTitle: string;
}

// DISC-4506 decision: 720p delivery on small screens, 1080p on large. Measured from the
// container rather than the viewport so the component stays layout-agnostic.
const HIGH_RES_MIN_WIDTH = 960;

export const HeroVideo = ({
  buttons,
  headingLine1,
  headingLine2,
  headingPosition,
  onCtaClick,
  onWatchNowClick,
  paragraph,
  paused = false,
  previewPlaybackId,
  stillImageUrlLarge,
  stillImageUrlSmall,
  termsAndConditions,
  videoTitle,
}: HeroVideoProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [maxResolution, setMaxResolution] = useState<'720p' | '1080p'>('720p');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((containerRef.current?.offsetWidth ?? 0) >= HIGH_RES_MIN_WIDTH) {
      setMaxResolution('1080p');
    }
  }, []);

  return (
    <div css={styles.container} ref={containerRef}>
      {/* The whole picture opens the takeover, not just the 48px button in the middle of a tall
          phone crop. The button stays as the keyboard and screen-reader control. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- pointer convenience only */}
      <div css={styles.media} onClick={onWatchNowClick}>
        {/* Decorative: the heading carries the message. Same frame the player shows first. */}
        <picture>
          {stillImageUrlLarge && (
            <source media={`(min-width: ${styles.BREAKPOINT_M}px)`} srcSet={stillImageUrlLarge} />
          )}
          <img
            alt=""
            css={styles.still}
            data-testid="hero-video-still"
            src={
              stillImageUrlSmall ??
              `https://image.mux.com/${previewPlaybackId}/thumbnail.webp?time=0`
            }
          />
        </picture>
        {!prefersReducedMotion && (
          <MuxPlayer
            autoPlay="muted"
            css={styles.player}
            loop
            maxResolution={maxResolution}
            metadata={{ player_name: 'browse-cmspages', video_title: videoTitle }}
            muted
            nohotkeys
            onPlaying={() => setPlaying(true)}
            paused={paused}
            playbackId={previewPlaybackId}
            playsInline
            streamType="on-demand"
            style={{ opacity: playing ? 1 : 0 }}
            thumbnailTime={0}
          />
        )}
        {headingPosition === 'hidden' ? (
          <h2 css={visuallyHidden}>
            {headingLine1}
            {headingLine2 && <br />}
            {headingLine2}
          </h2>
        ) : (
          <h2
            css={[
              styles.heading,
              headingPosition === 'top' ? styles.headingTop : styles.headingBottom,
            ]}
          >
            {/* Decorative blur layer behind the gradient; masked so it fades into the video. */}
            <div
              aria-hidden="true"
              css={[
                styles.blurLayer,
                headingPosition === 'top' ? styles.blurLayerTop : styles.blurLayerBottom,
              ]}
              data-testid="heading-backdrop"
            >
              <div css={styles.blurInner} />
            </div>
            {headingLine1}
            {headingLine2 && <br />}
            {headingLine2}
          </h2>
        )}
        <button
          aria-label="Watch the full video"
          css={styles.playButton}
          onClick={(e) => {
            // Don't let the click reach the media wrapper and open the takeover twice.
            e.stopPropagation();
            onWatchNowClick?.();
          }}
          type="button"
        >
          <PlayIcon />
        </button>
      </div>
      {(paragraph || termsAndConditions || buttons.length > 0) && (
        <div css={styles.copyRow}>
          {paragraph && <p css={styles.paragraph}>{paragraph}</p>}
          {buttons.length > 0 && (
            <div css={styles.ctaButtons}>
              {buttons.map((button) => (
                <Button
                  href={button.href}
                  key={button.href}
                  onClick={(e) => onCtaClick?.(button, e)}
                  secondary
                >
                  {button.text}
                </Button>
              ))}
            </div>
          )}
          {termsAndConditions && <p css={styles.terms}>{termsAndConditions}</p>}
        </div>
      )}
    </div>
  );
};
